import produce from 'immer';
import get from 'lodash/get';

import { isDurable, isGlobal, isTenant } from '../../internals/pipes';

import type { Pipe, PipeResponse } from '../../types/pipes.types';

const isInternalPipeRegex = /^(system:|configure_node:|load_nodeconfig:|merge_nodeconfig:)/;

function getDirection(pipe: Pipe) {
  // direction already in pipe metadata (from multinode script)
  const metadataDirection = get(pipe, 'config.original.metadata.direction');

  if (metadataDirection) {
    return metadataDirection;
  }

  // otherwise we use the old way (not multinode)
  const datasetSink = get(pipe, 'config.effective.sink.type') === 'dataset';

  const datasetSource = [
    'dataset',
    'merge',
    'union_datasets',
    'diff_datasets',
    'merge_datasets',
  ].includes(pipe?.config?.effective?.source?.type ?? '');

  if (datasetSink && datasetSource) {
    return 'internal';
  }

  if (datasetSink) {
    return 'inbound';
  }

  if (datasetSource) {
    return 'outbound';
  }

  return 'direct';
}

export function decoratePipe(rawPipe: PipeResponse) {
  return <Pipe>produce<PipeResponse, Pipe>(rawPipe, (pipe) => {
    const id = pipe._id;
    pipe.direction = getDirection(pipe);
    pipe.link = encodeURIComponent(id);
    pipe.origin = pipe.runtime.origin;
    if (pipe.origin == null) {
      // fallback until all nodes expose this
      pipe.origin = isInternalPipeRegex.test(id) ? 'system' : 'user';
    }
    pipe.isInternal = pipe.origin !== 'user';
    pipe.name = get(rawPipe, 'config.original.name', id); // sesam 2 doesn't have `original` section
    pipe.actionStatus = { status: null, errors: [] };
    pipe['config-warnings'] = get(rawPipe, 'runtime["config-errors"]', []).filter(
      (i) => i.level !== 'info'
    );
    pipe.pumpMode = get(rawPipe, 'config.effective.pump.mode', 'scheduled');
    pipe.tags = [];
    const userTags = get(rawPipe, 'config.original.metadata.tags');
    if (userTags) {
      if (Array.isArray(userTags)) {
        pipe.tags = pipe.tags.concat(userTags);
      } else {
        pipe.tags.push(userTags);
      }
    }
    pipe.global = isGlobal(rawPipe) ? 'Yes' : 'No';
    pipe.durable = isDurable(rawPipe) ? 'Yes' : 'No';
    pipe.tenant = isTenant(rawPipe) ? isTenant(rawPipe) : 'Not Tenant';
    pipe.lastRunStatus = pipe.runtime.success ? 'Succeeded' : 'Failed';
    if (rawPipe.config.effective.metadata && rawPipe.config.effective.metadata['$config-group']) {
      pipe.configGroup = rawPipe.config.effective.metadata['$config-group'];
    } else {
      pipe.configGroup = 'Default';
    }

    let transforms = get(pipe, 'config.effective.transform.transforms', []);
    if (transforms && !transforms.length) {
      transforms = [{ ...transforms }];
    }
    for (const transform of transforms) {
      const type = get(transform, 'type');
      const system = get(transform, 'system');
      if (type === 'rest') {
        pipe.transforms = system;
      }
    }
  });
}
